import Button from "common/Button";
import FormFields from "components/Form";
import React, { useContext, useEffect, useState } from "react";
import styles from "./requestPayout.module.css";
import { BurgerMenuContext } from "context/sidebar";
import { getAll, postData, SearchOptions, deleteData } from "utils/queries";
import toast from "react-hot-toast";
import { FaLock } from "react-icons/fa";
import { FaRegTrashCan } from "react-icons/fa6";

const RequestPayout = ({ getAllStripeAccounts, sidebarRef }) => {
  const [requestPayout, setRequestPayout] = useState({
    amount: 0,
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    selectedBankAccount: "",
    companyName: "",
    companyNumber: "",
    companyVATNumber: "",
    registeredAddress: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    postCode: "",
    accountType: "UK Bank Account",
    bankName: "",
    accountHolderName: "",
    accountHolderAddress: "",
    accountHolderAddressLine2: "",
    accountHolderAddressLine3: "",
    accountHolderCity: "",
    accountHolderPostCode: "",
    accountNumber: "",
    confirmAccountNumber: "",
    sortCode: "",
    confirmSortCode: "",
    organizationType: "company", // New field for organization type
  });
  const [payoutStatus, setPayoutStatus] = useState();
  const [showAddNewAccount, setShowAddNewAccount] = useState(false);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const burgerContext = useContext(BurgerMenuContext);

  const handleChange = (label) => (e) => {
    setRequestPayout((prevDetails) => ({
      ...prevDetails,
      [label]: e.target ? e.target.value : e,
    }));
  };

  const fetchOptions = async (search) => {
    try {
      const optionsResult = await SearchOptions(`ticket/search?key=${search}`);
      return optionsResult?.data?.data
        ?.filter((e) =>
          e?.event_name?.toLowerCase().includes(search.toLowerCase())
        )
        ?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
          starts: e?.starts,
          ends: e?.ends,
        }));
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  const fetchAccountsOptions = async (search) => {
    try {
      const optionsResult = await SearchOptions(`stripe/search?key=${search}`);
      return optionsResult?.data?.data
        ?.filter((e) =>
          e?.holder_name?.toLowerCase().includes(search.toLowerCase())
        )
        ?.map((e) => ({
          value: e?.id,
          label: `${e?.holder_name} (${e?.email})}`,
          stripe_id: e?.stripe_account_id,
        }));
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  // Delete bank account
  const handleDeleteBankAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to delete this bank account?")) {
      return;
    }

    try {
      setLoading(true);
      const result = await deleteData(`stripe/payout/bank-accounts?id=${accountId}`);
      
      if (result) {
        // toast.success("Bank account deleted successfully!");
        // Refresh the bank accounts list
        fetchExistingBankAccounts();
        
        // Clear selected account if it was the deleted one
        if (parseInt(requestPayout.selectedBankAccount) === accountId) {
          setRequestPayout(prev => ({
            ...prev,
            selectedBankAccount: ""
          }));
        }
      }
    } catch (error) {
      console.error("Error deleting bank account:", error);
      toast.error("Failed to delete bank account. Please try again.");
    } finally {
      setLoading(false);
    }
  };
    // Fetch existing bank accounts
  const fetchExistingBankAccounts = async () => {
    try {
      setLoading(true);
      const result = await getAll(`stripe/payout/bank-accounts`);      
      if (result) {
        setExistingAccounts(result?.data?.accounts || []);

      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      toast.error("Failed to load existing bank accounts");
    } finally {
      setLoading(false);
    }
  };

  // Initial fields (shown in first screenshot)
  const initialFields = [
    {
      label: "Amount",
      id: "amount",
      placeholder: "Enter Amount",
      type: "number",
      value: requestPayout?.amount,
      required: true,
      onChange: handleChange("amount"),
    },
  ];

  // Your Details fields
  const yourDetailsFields = [
    {
      label: "First Name",
      id: "firstName",
      placeholder: "Enter your first name",
      type: "text",
      value: requestPayout?.firstName,
      required: true,
      onChange: handleChange("firstName"),
    },
    {
      label: "Surname",
      id: "lastName",
      placeholder: "Enter your surname",
      type: "text",
      value: requestPayout?.lastName,
      required: true,
      onChange: handleChange("lastName"),
    },
    {
      label: "Email Address",
      id: "emailAddress",
      placeholder: "Enter the email address",
      type: "email",
      value: requestPayout?.emailAddress,
      required: true,
      onChange: handleChange("emailAddress"),
    },
    {
      label: "Phone Number",
      id: "phoneNumber",
      placeholder: "Enter a contact number",
      type: "tel",
      value: requestPayout?.phoneNumber,
      required: true,
      onChange: handleChange("phoneNumber"),
    },
  ];

  // Organisation Details fields
  const organisationFields = [
    {
      label: "Organization Type",
      id: "organizationType",
      type: "select",
      value: requestPayout?.organizationType,
      required: true,
      options: [
        { value: "company", label: "Company" },
        { value: "individual", label: "Individual" },
        { value: "sole_trader", label: "Sole Trader" },
        { value: "partnership", label: "Partnership" },
      ],
      onChange: handleChange("organizationType"),
    },
    {
      label: "Company Name",
      id: "companyName",
      placeholder: "Enter your registered company name",
      type: "text",
      value: requestPayout?.companyName,
      required: true,
      onChange: handleChange("companyName"),
    },
    {
      label: "Company Number",
      id: "companyNumber",
      placeholder: "Enter your registered company number",
      type: "text",
      value: requestPayout?.companyNumber,
      required: false,
      onChange: handleChange("companyNumber"),
    },
    {
      label: "Company VAT Number (If Applicable)",
      id: "companyVATNumber",
      placeholder: "Enter your company VAT number",
      type: "text",
      value: requestPayout?.companyVATNumber,
      required: false,
      onChange: handleChange("companyVATNumber"),
    },
    {
      label: "Registered Address",
      id: "registeredAddress",
      placeholder: "Address",
      type: "text",
      value: requestPayout?.registeredAddress,
      required: true,
      onChange: handleChange("registeredAddress"),
    },
    {
      label: "",
      id: "addressLine2",
      placeholder: "Address 2",
      type: "text",
      value: requestPayout?.addressLine2,
      required: false,
      onChange: handleChange("addressLine2"),
      hideLabel: true,
    },
    {
      label: "",
      id: "addressLine3",
      placeholder: "Address 3",
      type: "text",
      value: requestPayout?.addressLine3,
      required: false,
      onChange: handleChange("addressLine3"),
      hideLabel: true,
    },
    {
      label: "",
      id: "city",
      placeholder: "City",
      type: "text",
      value: requestPayout?.city,
      required: false,
      onChange: handleChange("city"),
      hideLabel: true,
    },
    {
      label: "",
      id: "postCode",
      placeholder: "Post Code",
      type: "text",
      value: requestPayout?.postCode,
      required: false,
      onChange: handleChange("postCode"),
      hideLabel: true,
    },
  ];

  // Bank Account Details fields
  const bankAccountFields = [
    {
      label: "Account Type",
      id: "accountType",
      type: "select",
      value: requestPayout?.accountType,
      required: true,
           options: [
        { value: "UK Bank Account", label: "UK Bank Account" },
        { value: "International Bank Account", label: "International Bank Account" },
      ],
      // options: [
      //   { value: "checking", label: "Checking Account" },
      //   { value: "savings", label: "Savings Account" },
      //   { value: "business", label: "Business Account" },
      // ],
      onChange: handleChange("accountType"),
    },
    {
      label: "Bank Name",
      id: "bankName",
      placeholder: "HSBC, Natwest, Barclays, etc",
      type: "text",
      value: requestPayout?.bankName,
      required: true,
      onChange: handleChange("bankName"),
    },
    {
      label: "Account Holder's Name",
      id: "accountHolderName",
      placeholder: "This is the name of the Company that holds the bank account",
      type: "text",
      value: requestPayout?.accountHolderName,
      required: true,
      onChange: handleChange("accountHolderName"),
    },
    {
      label: "Account Holder's Address",
      id: "accountHolderAddress",
      placeholder: "Address",
      type: "text",
      value: requestPayout?.accountHolderAddress,
      required: true,
      onChange: handleChange("accountHolderAddress"),
    },
    {
      label: "",
      id: "accountHolderAddressLine2",
      placeholder: "Address 2",
      type: "text",
      value: requestPayout?.accountHolderAddressLine2,
      required: false,
      onChange: handleChange("accountHolderAddressLine2"),
      hideLabel: true,
    },
    {
      label: "",
      id: "accountHolderAddressLine3",
      placeholder: "Address 3",
      type: "text",
      value: requestPayout?.accountHolderAddressLine3,
      required: false,
      onChange: handleChange("accountHolderAddressLine3"),
      hideLabel: true,
    },
    {
      label: "",
      id: "accountHolderCity",
      placeholder: "City",
      type: "text",
      value: requestPayout?.accountHolderCity,
      required: false,
      onChange: handleChange("accountHolderCity"),
      hideLabel: true,
    },
    {
      label: "",
      id: "accountHolderPostCode",
      placeholder: "Post Code",
      type: "text",
      value: requestPayout?.accountHolderPostCode,
      required: false,
      onChange: handleChange("accountHolderPostCode"),
      hideLabel: true,
    },
    {
      label: "Account Number",
      id: "accountNumber",
      placeholder: "Enter the account number",
      type: "text",
      value: requestPayout?.accountNumber,
      required: true,
      onChange: handleChange("accountNumber"),
    },
    {
      label: "Confirm Account Number",
      id: "confirmAccountNumber",
      placeholder: "Please confirm the account number",
      type: "text",
      value: requestPayout?.confirmAccountNumber,
      required: true,
      onChange: handleChange("confirmAccountNumber"),
    },
    {
      label: "Sort Code",
      id: "sortCode",
      placeholder: "Enter the sort code",
      type: "text",
      value: requestPayout?.sortCode,
      required: true,
      onChange: handleChange("sortCode"),
    },
    {
      label: "Confirm Sort Code",
      id: "confirmSortCode",
      placeholder: "Please confirm the sort code",
      type: "text",
      value: requestPayout?.confirmSortCode,
      required: true,
      onChange: handleChange("confirmSortCode"),
    },
  ];

  const getAllPayoutStatus = async () => {
    try {
      const result = await getAll(`stripe/finance/sales`);
      setPayoutStatus(result?.data || []);
    } catch (error) {
      console.error("Error fetching payout status:", error);
    }
  };

  useEffect(() => {
    getAllPayoutStatus();
    fetchExistingBankAccounts();
  }, []);

  const handlePayout = async (e) => {
    e?.preventDefault();

    if (!payoutStatus) {
      return alert("Payout status is not available. Please try again.");
    }

    const requestedAmount = parseFloat(requestPayout?.amount);
    const clearedAmount = payoutStatus?.cleared_price || 0;

    if (!requestedAmount || requestedAmount <= 0) {
      return alert("Please enter a valid amount.");
    }
    if (requestedAmount < 5) {
      return toast.error("The minimum payout amount is £5.");
    }
    if (requestedAmount > clearedAmount) {
      return toast.error(`Requested amount exceeds the cleared balance.`);
    }

    // Validation for new account fields
    if (showAddNewAccount) {
      if (requestPayout.accountNumber !== requestPayout.confirmAccountNumber) {
        return toast.error("Account numbers do not match.");
      }
      if (requestPayout.sortCode !== requestPayout.confirmSortCode) {
        return toast.error("Sort codes do not match.");
      }
    }

    try {
      setLoading(true);
      
      let payload;
      
      if (showAddNewAccount) {
        // New account payload
        payload = {
          amount: requestedAmount,
          payout_meta: {
            first_name: requestPayout.firstName,
            last_name: requestPayout.lastName,
            email: requestPayout.emailAddress,
            phone: requestPayout.phoneNumber,
          },
          payout_details: {
            organization_type: requestPayout.organizationType?.value || requestPayout.organizationType,
            company_name: requestPayout.companyName,
            company_number: requestPayout.companyNumber,
            vat_number: requestPayout.companyVATNumber,
            address_line1: requestPayout.registeredAddress,
            address_line2: requestPayout.addressLine2,
            address_line3: requestPayout.addressLine3,
            city: requestPayout.city,
            postcode: requestPayout.postCode,
          },
          payout_account_details: {
            account_type: requestPayout.accountType?.value || requestPayout.accountType,
            bank_name: requestPayout.bankName,
            account_holder_name: requestPayout.accountHolderName,
            holder_address_line1: requestPayout.accountHolderAddress,
            holder_address_line2: requestPayout.accountHolderAddressLine2,
            holder_address_line3: requestPayout.accountHolderAddressLine3,
            holder_city: requestPayout.accountHolderCity,
            holder_postcode: requestPayout.accountHolderPostCode,
            account_number: requestPayout.accountNumber,
            sort_code: requestPayout.sortCode,
          },
        };
      } else {
        // Existing account payload
        const selectedAccount = existingAccounts.find(
          (acc) => acc.payout_account_id === parseInt(requestPayout.selectedBankAccount)
        );
        
        if (!selectedAccount) {
          return toast.error("Please select a bank account.");
        }
        
        payload = {
          amount: requestedAmount,
          payout_meta: {
            first_name: requestPayout.firstName,
            last_name: requestPayout.lastName,
            email: requestPayout.emailAddress,
            phone: requestPayout.phoneNumber,
          },
          payout_details_id: selectedAccount.payout_details_id,
          payout_account_id: selectedAccount.payout_account_id,
        };
      }

      const requestedPayout = await postData(`stripe/payout`, payload);

      if (requestedPayout) {
        getAllPayoutStatus();
        burgerContext.setShowSideBar(false);
        // toast.success("Payout request submitted successfully!");
        
        // Reset form
        setRequestPayout({
          amount: 0,
          firstName: "",
          lastName: "",
          emailAddress: "",
          phoneNumber: "",
          selectedBankAccount: "",
          companyName: "",
          companyNumber: "",
          companyVATNumber: "",
          registeredAddress: "",
          addressLine2: "",
          addressLine3: "",
          city: "",
          postCode: "",
          accountType: "checking",
          bankName: "",
          accountHolderName: "",
          accountHolderAddress: "",
          accountHolderAddressLine2: "",
          accountHolderAddressLine3: "",
          accountHolderCity: "",
          accountHolderPostCode: "",
          accountNumber: "",
          confirmAccountNumber: "",
          sortCode: "",
          confirmSortCode: "",
          organizationType: "company",
        });
        setShowAddNewAccount(false);
      }
    } catch (error) {
      console.error("Error processing payout:", error);
      toast.error("Error processing payout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (field.hideLabel) {
      return (
        <div key={field.id} className={styles.fieldWrapper}>
          <FormFields Fields={[field]} />
        </div>
      );
    }
    return (
      <div key={field.id} className={styles.fieldWrapper}>
        <FormFields Fields={[field]} />
      </div>
    );
  };

  return (
    <div className={styles.container} id="requestPayout" ref={sidebarRef}>
      <div className={styles.header}>
        <div className={styles.label}>Request Payout</div>
        <img
          src="/icons/cancel.svg"
          alt="cancel"
          onClick={() => {
            burgerContext.setShowSideBar(false);
          }}
        />
      </div>
      
      <div className={styles.subLabel}>
        Complete the form below to request a payout for Event Name . Please get
        in touch with your Account Manager or with accounts@flow.com if you
        require assistance.
      </div>

      <form onSubmit={handlePayout}>
        {/* Amount Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Amount</h3>
            <div className={styles.availableAmount}>£{payoutStatus?.cleared_price || 0} Available to Payout</div>
          </div>
          {initialFields.map((field) => renderField(field))}
        </div>

        {/* Your Details Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Your Details</h3>
            <div className={styles.sectionSubtext}>
              Please provide your details so we can contact you if there are any issues with the payout.
            </div>
          </div>
          {yourDetailsFields.map((field) => renderField(field))}
        </div>

        {/* Select Bank Account Section */}
        <div className={styles.section}>
          {!showAddNewAccount && (
          <div className={styles.sectionHeader}>
            <h3>Select Bank Account</h3>
          </div>

          )}
          
          {loading ? (
            <div>Loading bank accounts...</div>
          ) : !showAddNewAccount ? (
            <div className={styles.bankAccountList}>
              {existingAccounts.length > 0 ? (
                existingAccounts.map((account) => (
                  <div key={account.payout_account_id} className={styles.bankAccountItem}>
                    <input
                      type="radio"
                      id={`account-${account.payout_account_id}`}
                      name="selectedBankAccount"
                      value={account.payout_account_id}
                      onChange={handleChange("selectedBankAccount")}
                    />
                    <label htmlFor={`account-${account.payout_account_id}`} className={styles.bankAccountLabel}>
                      {account.account_holder_name} (****{account.last4})
                    </label>
                    <button 
                      type="button" 
                      className={styles.removeButton}
                      onClick={() => handleDeleteBankAccount(account.payout_account_id)}
                      disabled={loading}
                    >
                      <FaRegTrashCan />
                    </button>
                  </div>
                ))
              ) : (
                <div className={styles.noAccounts}>No existing bank accounts found.</div>
              )}
              
              <Button
                type="button"
                label="Add New Account"
                onClick={() => setShowAddNewAccount(true)}
                buttonOverrideStyle={styles.addNewAccountButton}
              />
            </div>
          ) : (
            <>
              {/* Organisation Details */}
              <div className={styles.subsection}>
                <div className={styles.subsectionHeader}>
                  <h4>Organisation Details</h4>
                </div>
                {organisationFields.map((field) => renderField(field))}
              </div>

              {/* Bank Account Details */}
              <div className={styles.subsection}>
                <div className={styles.subsectionHeader}>
                  <h4>Bank Account Details</h4>
                </div>
                {bankAccountFields.map((field) => renderField(field))}
              </div>
              
              <Button
                type="button"
                label="Back to Existing Accounts"
                onClick={() => setShowAddNewAccount(false)}
                buttonOverrideStyle={styles.backButton}
              />
            </>
          )}
        </div>

        <div className={styles.actionButtons}>
          <div className={styles.actionLabel}>
            <FaLock/> 
            Secure
          </div>
          <Button
            type="submit"
            label={loading ? "Processing..." : "Request Payout"}
            disabled={loading}
            buttonOverrideStyle={styles.buttonOverrideStyle}
          />
        </div>
      </form>
    </div>
  );
};

export default RequestPayout;